// iter-121.116 Phase 4 — "For Your Information" tabs for SA Home.
//
// Wraps three perspectives in a Tabs component so the SA sees ONE lens
// at a time, replacing the wall of redundant KPIs in the original mockup.
//   • Business    → revenue, active orgs, signups, MRR growth
//   • Usage & AI  → active users, queries, AI spend, queries/day
//   • Reliability → uptime, p95 latency, error rate, data processed
//
// Each tab fetches `/api/home/cockpit/kpi-hero?tab=...` for the 4 cards,
// and `/api/home/cockpit/top-orgs?metric=...` for the ranked Org list.
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useCockpitScope } from "@/lib/cockpit-scope";
import KpiCard from "./KpiCard";
import WhatsNewWidget from "../WhatsNewWidget";

const TABS = [
    { value: "business",    label: "Business",    metric: "revenue" },
    { value: "usage_ai",    label: "Usage & AI",  metric: "ai_spend" },
    { value: "reliability", label: "Reliability", metric: "queries" },
    { value: "whats_new",   label: "What's new" },
];

function MetricLabel({ metric }) {
    const map = {
        revenue:  "Top 10 Orgs by Revenue",
        ai_spend: "Top 10 Orgs by AI Spend",
        queries:  "Top 10 Orgs by Queries",
        users:    "Top 10 Orgs by Active Users",
    };
    return <span>{map[metric] || metric}</span>;
}

function TopOrgsTable({ metric, queryString }) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        setRows(null);
        api.get(`/home/cockpit/top-orgs?metric=${metric}&limit=10&${queryString}`)
            .then((r) => setRows(r.data?.rows || []))
            .catch(() => setRows([]));
    }, [metric, queryString]);

    const valueFormat = useMemo(() => {
        if (metric === "revenue" || metric === "ai_spend") {
            return (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        }
        return (n) => Number(n).toLocaleString();
    }, [metric]);

    if (rows === null) {
        return <div className="text-xs text-muted-foreground py-6 text-center">Loading top orgs…</div>;
    }
    if (rows.length === 0) {
        return <div className="text-xs text-muted-foreground py-6 text-center"
                    data-testid={`top-orgs-empty-${metric}`}>No data in the selected scope.</div>;
    }
    return (
        <div className="border border-border rounded-sm overflow-hidden"
             data-testid={`top-orgs-table-${metric}`}>
            <div className="px-3 py-2 bg-secondary/30 flex items-center justify-between border-b border-border">
                <div className="eyebrow text-[10px]"><MetricLabel metric={metric} /></div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    {rows.length} orgs
                </div>
            </div>
            <table className="w-full text-[12px]">
                <thead className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="text-left px-3 py-1.5 w-8">#</th>
                        <th className="text-left px-3 py-1.5">Organisation</th>
                        <th className="text-right px-3 py-1.5">Value</th>
                        <th className="text-right px-3 py-1.5 hidden sm:table-cell">Secondary</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.org_id} className="border-t border-border"
                            data-testid={`top-orgs-row-${metric}-${i}`}>
                            <td className="px-3 py-1.5 text-muted-foreground">{i + 1}</td>
                            <td className="px-3 py-1.5 font-medium">{r.org_name}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                                {valueFormat(r.value)}
                            </td>
                            <td className="px-3 py-1.5 text-right text-muted-foreground hidden sm:table-cell">
                                {r.secondary
                                    ? `${r.secondary.value.toLocaleString()} ${r.secondary.label}`
                                    : "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function TabBody({ tab, metric }) {
    const { queryString } = useCockpitScope();
    const [cells, setCells] = useState(null);

    useEffect(() => {
        setCells(null);
        api.get(`/home/cockpit/kpi-hero?tab=${tab}&${queryString}`)
            .then((r) => setCells(r.data?.cells || []))
            .catch(() => setCells([]));
    }, [tab, queryString]);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
                 data-testid={`sa-info-kpi-grid-${tab}`}>
                {cells === null
                    ? [1, 2, 3, 4].map((i) => <div key={i} className="h-32 shimmer rounded-sm" />)
                    : cells.map((c) => <KpiCard key={c.key} cell={c} />)}
            </div>
            <TopOrgsTable metric={metric} queryString={queryString} />
        </div>
    );
}

export default function SaInfoTabs({ onEmpty }) {
    const [tab, setTab] = useState("business");
    useEffect(() => { onEmpty && onEmpty(false); }, [onEmpty]);
    return (
        <div data-testid="sa-info-tabs">
            <div className="eyebrow text-[10px] mb-2">For Your Information</div>
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="sa-info-tabs-list">
                    {TABS.map((t) => (
                        <TabsTrigger key={t.value} value={t.value}
                                     data-testid={`sa-info-tab-${t.value}`}>
                            {t.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
                {TABS.map((t) => (
                    <TabsContent key={t.value} value={t.value} className="mt-3"
                                 data-testid={`sa-info-tab-content-${t.value}`}>
                        {/* Only render the active tab's body to avoid 3x fetches. */}
                        {tab === t.value && (t.value === "whats_new"
                            ? <WhatsNewWidget />
                            : <TabBody tab={t.value} metric={t.metric} />)}
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}
